import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useSettings } from '../contexts/SettingsContext';
import { AVAILABLE_LANGUAGES } from '../config/appConfig';
import * as SpeciesService from '../services/SpeciesService';
import type { SpeciesWithTranslation } from '../services/SpeciesService';
import { 
  db, 
  generateId, 
  markDefaultAsRemoved, 
  REMOVED_SPECIES_KEY,
  REMOVED_METHODS_KEY,
  REMOVED_TACKLE_KEY 
} from '../services/FishingDB';
import type { FishingMethod, Tackle } from '../entities/FishingEntities';
import type { Species } from '../entities/SpeciesEntities';
import ExportModal from './ExportModal';
import ConfirmDialog from './ConfirmDialog';
import Toast from './Toast';

interface SettingsPanelProps {
  showTitle?: boolean;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({ showTitle = true }) => {
  const { t, language, setLanguage } = useLanguage();
  const { unitSystem, setUnitSystem, defaultSpecies, setDefaultSpecies, defaultMethod, setDefaultMethod, defaultTackle, setDefaultTackle } = useSettings();
  const [showExportModal, setShowExportModal] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'info' | 'success' | 'error'>('info');
  const [speciesList, setSpeciesList] = useState<SpeciesWithTranslation[]>([]);
  const [methodsList, setMethodsList] = useState<FishingMethod[]>([]);
  const [tackleList, setTackleList] = useState<Tackle[]>([]);
  const [newMethodName, setNewMethodName] = useState('');
  const [newTackleName, setNewTackleName] = useState('');
  const [newSpeciesName, setNewSpeciesName] = useState('');
  const [showAddMethod, setShowAddMethod] = useState(false);
  const [showAddTackle, setShowAddTackle] = useState(false);
  const [showAddSpecies, setShowAddSpecies] = useState(false);
  
  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    variant: 'danger' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    variant: 'warning'
  });

  // Load species with translations
  const loadSpecies = useCallback(async () => {
    try {
      const allSpecies = await SpeciesService.getAllSpecies((key: string) => t[key as keyof typeof t] as string, language);
      setSpeciesList(allSpecies);
    } catch (error) {
      console.error('Error loading species:', error);
    }
  }, [t, language]);

  // Load fishing methods
  const loadMethods = useCallback(async () => {
    try {
      const methods = await db.methods.orderBy('createdAt').toArray();
      setMethodsList(methods);
    } catch (error) {
      console.error('Error loading methods:', error);
    }
  }, []);

  // Load tackle
  const loadTackle = useCallback(async () => {
    try {
      const tackle = await db.tackle.orderBy('createdAt').toArray();
      setTackleList(tackle);
    } catch (error) {
      console.error('Error loading tackle:', error);
    }
  }, []);

  useEffect(() => {
    loadSpecies();
    loadMethods();
    loadTackle();
  }, [loadSpecies, loadMethods, loadTackle]);

  // Add new fishing method
  const handleAddMethod = async () => {
    if (!newMethodName.trim()) return;
    
    try {
      await db.methods.add({
        id: generateId(),
        key: newMethodName.trim(), // For custom, key = name
        isCustom: true,
        createdAt: Date.now()
      });
      setNewMethodName('');
      setShowAddMethod(false);
      loadMethods();
      showToast(`${t.fishingMethod} "${newMethodName}" ${t.added}`, 'success');
    } catch (error) {
      console.error('Error adding method:', error);
      showToast(t.error, 'error');
    }
  };

  // Remove fishing method
  const handleRemoveMethod = async (method: FishingMethod) => {
    const methodName = method.isCustom ? method.key : t[method.key as keyof typeof t] as string;
    
    setConfirmDialog({
      isOpen: true,
      title: t.confirmRemove,
      message: `${t.removeMethodWarning}\n\n${t.catchesWillRemain}`,
      variant: 'warning',
      onConfirm: async () => {
        try {
          // If it's a default, mark it as removed in localStorage
          if (!method.isCustom) {
            markDefaultAsRemoved(REMOVED_METHODS_KEY, method.key);
          }
          
          await db.methods.delete(method.id);
          loadMethods();
          showToast(`${t.fishingMethod} "${methodName}" ${t.removed}`, 'success');
        } catch (error) {
          console.error('Error removing method:', error);
          showToast(t.error, 'error');
        }
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  // Add new tackle
  const handleAddTackle = async () => {
    if (!newTackleName.trim()) return;
    
    try {
      await db.tackle.add({
        id: generateId(),
        key: newTackleName.trim(), // For custom, key = name
        isCustom: true,
        createdAt: Date.now()
      });
      setNewTackleName('');
      setShowAddTackle(false);
      loadTackle();
      showToast(`${t.tackle} "${newTackleName}" ${t.added}`, 'success');
    } catch (error) {
      console.error('Error adding tackle:', error);
      showToast(t.error, 'error');
    }
  };

  // Remove tackle
  const handleRemoveTackle = async (tackle: Tackle) => {
    const tackleName = tackle.isCustom ? tackle.key : t[tackle.key as keyof typeof t] as string;
    
    setConfirmDialog({
      isOpen: true,
      title: t.confirmRemove,
      message: `${t.removeTackleWarning}\n\n${t.catchesWillRemain}`,
      variant: 'warning',
      onConfirm: async () => {
        try {
          // If it's a default, mark it as removed in localStorage
          if (!tackle.isCustom) {
            markDefaultAsRemoved(REMOVED_TACKLE_KEY, tackle.key);
          }
          
          await db.tackle.delete(tackle.id);
          loadTackle();
          showToast(`${t.tackle} "${tackleName}" ${t.removed}`, 'success');
        } catch (error) {
          console.error('Error removing tackle:', error);
          showToast(t.error, 'error');
        }
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  // Add new species
  const handleAddSpecies = async () => {
    if (!newSpeciesName.trim()) return;
    
    try {
      await SpeciesService.addCustomSpecies({
        name: newSpeciesName.trim(),
        icon: '🐟'
      });
      setNewSpeciesName('');
      setShowAddSpecies(false);
      loadSpecies();
      showToast(`${t.species || 'Species'} "${newSpeciesName}" ${t.added}`, 'success');
    } catch (error) {
      console.error('Error adding species:', error);
      showToast(t.error, 'error');
    }
  };

  // Remove species
  const handleRemoveSpecies = async (species: Species) => {
    const speciesName = species.isCustom ? species.key : t[species.key as keyof typeof t] as string;
    
    setConfirmDialog({
      isOpen: true,
      title: t.confirmRemove,
      message: `${t.removeSpeciesWarning}\n\n${t.catchesWillRemain}`,
      variant: 'danger', // Species removal is more critical
      onConfirm: async () => {
        try {
          // If it's a default, mark it as removed in localStorage
          if (!species.isCustom) {
            markDefaultAsRemoved(REMOVED_SPECIES_KEY, species.key);
          }
          
          await db.species.delete(species.id);
          loadSpecies();
          showToast(`${t.species} "${speciesName}" ${t.removed}`, 'success');
        } catch (error) {
          console.error('Error removing species:', error);
          showToast(t.error, 'error');
        }
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      }
    });
  };  const showToast = (message: string, type: 'info' | 'success' | 'error' = 'info') => {
    setToastMessage(message);
    setToastType(type);
    setTimeout(() => setToastMessage(''), 3000);
  };

  return (
    <div className="space-y-6">
      {showTitle && (
        <h2 className="text-xl font-bold text-white mb-4">⚙️ {t.settings}</h2>
      )}

      {/* Language Selection */}
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">
          🌐 {t.language}
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {AVAILABLE_LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => setLanguage(lang.code)}
              className={`
                px-3 py-2 rounded-lg text-sm font-medium transition-colors
                ${
                  language === lang.code
                    ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/50'
                    : 'bg-white/5 text-white/70 border border-white/10 hover:bg-white/10 hover:text-white'
                }
              `}
            >
              <span className="mr-1.5">{lang.flag}</span>
              {lang.name}
            </button>
          ))}
        </div>
      </div>

      {/* Unit System Selection */}
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">
          📏 {t.units}
        </label>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setUnitSystem('metric')}
            className={`
              px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
              ${
                unitSystem === 'metric'
                  ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/50'
                  : 'bg-white/5 text-white/70 border border-white/10 hover:bg-white/10 hover:text-white'
              }
            `}
          >
            <div>{t.metric}</div>
            <div className="text-xs opacity-70 mt-0.5">°C • m/s</div>
          </button>
          <button
            onClick={() => setUnitSystem('imperial')}
            className={`
              px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
              ${
                unitSystem === 'imperial'
                  ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/50'
                  : 'bg-white/5 text-white/70 border border-white/10 hover:bg-white/10 hover:text-white'
              }
            `}
          >
            <div>{t.imperial}</div>
            <div className="text-xs opacity-70 mt-0.5">°F • mph</div>
          </button>
        </div>
      </div>

      {/* Species Management */}
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">
          🐟 {t.manageSpecies}
        </label>
        <p className="text-xs text-slate-400 mb-2">
          💡 {t.clickToSetDefault || 'Klicka på en art för att sätta som standard'}
        </p>
        <div className="space-y-2">
          {/* Grid of species chips */}
          <div className="grid grid-cols-2 gap-2">
            {speciesList.map((species) => (
              <div
                key={species.id}
                className={`
                  flex items-center justify-between px-3 py-2 rounded-lg transition-colors cursor-pointer
                  ${
                    defaultSpecies === species.key
                      ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/50'
                      : 'bg-white/5 border border-white/10 hover:bg-white/10'
                  }
                `}
                onClick={() => setDefaultSpecies(species.key as any)}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="flex-shrink-0">{species.icon}</span>
                  <span className="text-white text-sm truncate">{species.name}</span>
                  {!species.isCustom && (
                    <span className="text-xs text-slate-400 flex-shrink-0">({t.defaultLabel})</span>
                  )}
                  {defaultSpecies === species.key && (
                    <span className="text-xs text-cyan-300 flex-shrink-0">⭐</span>
                  )}
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveSpecies(species);
                  }}
                  className="text-red-400 hover:text-red-300 transition-colors flex-shrink-0 ml-2"
                  title={t.removed}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
          
          {/* Add new species - separate row */}
          {showAddSpecies ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={newSpeciesName}
                onChange={(e) => setNewSpeciesName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddSpecies()}
                placeholder={t.species || 'Species'}
                className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-cyan-500/50"
                autoFocus
              />
              <button
                onClick={handleAddSpecies}
                className="px-4 py-2 bg-cyan-500/20 text-cyan-300 border border-cyan-500/50 hover:bg-cyan-500/30 rounded-lg transition-colors"
              >
                ✓
              </button>
              <button
                onClick={() => {
                  setShowAddSpecies(false);
                  setNewSpeciesName('');
                }}
                className="px-4 py-2 bg-white/5 text-white/70 border border-white/10 hover:bg-white/10 rounded-lg transition-colors"
              >
                ✕
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowAddSpecies(true)}
              className="w-full px-4 py-2 bg-white/5 text-white/70 border border-white/10 hover:bg-white/10 hover:text-white rounded-lg text-sm font-medium transition-colors"
            >
              + {t.addSpecies}
            </button>
          )}
        </div>
      </div>

      {/* Fishing Methods Management */}
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">
          🎣 {t.manageMethods}
        </label>
        <p className="text-xs text-slate-400 mb-2">
          💡 {t.clickToSetDefault || 'Klicka på en metod för att sätta som standard'}
        </p>
        <div className="space-y-2">
          {/* Grid of method chips */}
          <div className="grid grid-cols-2 gap-2">
            {methodsList.map((method) => {
              const methodName = method.isCustom ? method.key : (t[method.key as keyof typeof t] as string);
              return (
                <div
                  key={method.id}
                  className={`
                    flex items-center justify-between px-3 py-2 rounded-lg transition-colors cursor-pointer
                    ${
                      defaultMethod === method.key
                        ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/50'
                        : 'bg-white/5 border border-white/10 hover:bg-white/10'
                    }
                  `}
                  onClick={() => setDefaultMethod(method.key)}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {method.icon && <span className="flex-shrink-0">{method.icon}</span>}
                    <span className="text-white text-sm truncate">{methodName}</span>
                    {!method.isCustom && (
                      <span className="text-xs text-slate-400 flex-shrink-0">({t.defaultLabel})</span>
                    )}
                    {defaultMethod === method.key && (
                      <span className="text-xs text-cyan-300 flex-shrink-0">⭐</span>
                    )}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveMethod(method);
                    }}
                    className="text-red-400 hover:text-red-300 transition-colors flex-shrink-0 ml-2"
                    title={t.removed}
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>
          
          {/* Add new method - separate row */}
          {showAddMethod ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={newMethodName}
                onChange={(e) => setNewMethodName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddMethod()}
                placeholder={t.fishingMethod}
                className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-cyan-500/50"
                autoFocus
              />
              <button
                onClick={handleAddMethod}
                className="px-4 py-2 bg-cyan-500/20 text-cyan-300 border border-cyan-500/50 hover:bg-cyan-500/30 rounded-lg transition-colors"
              >
                ✓
              </button>
              <button
                onClick={() => {
                  setShowAddMethod(false);
                  setNewMethodName('');
                }}
                className="px-4 py-2 bg-white/5 text-white/70 border border-white/10 hover:bg-white/10 rounded-lg transition-colors"
              >
                ✕
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowAddMethod(true)}
              className="w-full px-4 py-2 bg-white/5 text-white/70 border border-white/10 hover:bg-white/10 hover:text-white rounded-lg text-sm font-medium transition-colors"
            >
              + {t.addMethod}
            </button>
          )}
        </div>
      </div>

      {/* Tackle Management */}
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">
          🪝 {t.manageTackle}
        </label>
        <p className="text-xs text-slate-400 mb-2">
          💡 {t.clickToSetDefaultMultiple || 'Klicka för att sätta som standard (kan välja flera)'}
        </p>
        <div className="space-y-2">
          {/* Grid of tackle chips */}
          <div className="grid grid-cols-2 gap-2">
            {tackleList.map((tackle) => {
              const tackleName = tackle.isCustom ? tackle.key : (t[tackle.key as keyof typeof t] as string);
              const isSelected = defaultTackle.includes(tackle.key);
              return (
                <div
                  key={tackle.id}
                  className={`
                    flex items-center justify-between px-3 py-2 rounded-lg transition-colors cursor-pointer
                    ${
                      isSelected
                        ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/50'
                        : 'bg-white/5 border border-white/10 hover:bg-white/10'
                    }
                  `}
                  onClick={() => {
                    if (isSelected) {
                      setDefaultTackle(defaultTackle.filter(t => t !== tackle.key));
                    } else {
                      setDefaultTackle([...defaultTackle, tackle.key]);
                    }
                  }}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {tackle.icon && <span className="flex-shrink-0">{tackle.icon}</span>}
                    <span className="text-white text-sm truncate">{tackleName}</span>
                    {!tackle.isCustom && (
                      <span className="text-xs text-slate-400 flex-shrink-0">({t.defaultLabel})</span>
                    )}
                    {isSelected && (
                      <span className="text-xs text-cyan-300 flex-shrink-0">⭐</span>
                    )}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveTackle(tackle);
                    }}
                    className="text-red-400 hover:text-red-300 transition-colors flex-shrink-0 ml-2"
                    title={t.removed}
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>
          
          {/* Add new tackle - separate row */}
          {showAddTackle ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={newTackleName}
                onChange={(e) => setNewTackleName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddTackle()}
                placeholder={t.tackle}
                className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-cyan-500/50"
                autoFocus
              />
              <button
                onClick={handleAddTackle}
                className="px-4 py-2 bg-cyan-500/20 text-cyan-300 border border-cyan-500/50 hover:bg-cyan-500/30 rounded-lg transition-colors"
              >
                ✓
              </button>
              <button
                onClick={() => {
                  setShowAddTackle(false);
                  setNewTackleName('');
                }}
                className="px-4 py-2 bg-white/5 text-white/70 border border-white/10 hover:bg-white/10 rounded-lg transition-colors"
              >
                ✕
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowAddTackle(true)}
              className="w-full px-4 py-2 bg-white/5 text-white/70 border border-white/10 hover:bg-white/10 hover:text-white rounded-lg text-sm font-medium transition-colors"
            >
              + {t.addCustomTackle}
            </button>
          )}
        </div>
      </div>

      {/* Export & Backup */}
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">
          {t.exportBackup}
        </label>
        <button
          onClick={() => setShowExportModal(true)}
          className="w-full px-4 py-3 bg-white/5 text-white border border-white/10 hover:bg-white/10 hover:border-white/20 rounded-lg text-sm font-medium transition-colors"
        >
          {t.exportImportData}
        </button>
        <p className="text-xs text-slate-400 mt-2">
          💡 {t.offlineDataSync}
        </p>
      </div>

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onSuccess={showToast}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={t.confirmRemove}
        cancelText={t.cancel}
        variant={confirmDialog.variant}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
      />

      {/* Toast */}
      {toastMessage && (
        <Toast message={toastMessage} type={toastType} />
      )}
    </div>
  );
};

export default SettingsPanel;
